import {
  POST_SMOOTHING_SUPPRESSION_THRESHOLD,
  FINAL_SMOOTHING_WINDOW_POINTS,
  FINAL_SMOOTHING_GAUSSIAN_SPREAD,
  HARD_BRAKING_THRESHOLD_MS2,
  HARD_ACCELERATION_THRESHOLD_MS2,
  SIGNIFICANT_COASTING_THRESHOLD,
} from './constants'
import { type CoastingPeriod } from './types'

/**
 * Apply final Gaussian smoothing for visual cohesion.
 *
 * Why lean-angle-aware? Different riding modes (straight vs corner)
 * need separation - don't let corner coasting affect straight sections.
 */
export const applyFinalIntensitySmoothing = (
  periods: CoastingPeriod[]
): CoastingPeriod[] => {
  if (periods.length < 3) return periods

  const smoothedPeriods = [...periods]

  for (let i = 0; i < smoothedPeriods.length; i++) {
    const currentPeriod = smoothedPeriods[i]
    const prevPeriod = i > 0 ? smoothedPeriods[i - 1] : undefined
    const nextPeriod =
      i < smoothedPeriods.length - 1 ? smoothedPeriods[i + 1] : undefined

    const isHardBraking =
      currentPeriod.smoothedAcceleration < HARD_BRAKING_THRESHOLD_MS2
    const isHardAcceleration =
      currentPeriod.smoothedAcceleration >= HARD_ACCELERATION_THRESHOLD_MS2

    const inCoastingSection =
      prevPeriod &&
      nextPeriod &&
      prevPeriod.coastingIntensity > SIGNIFICANT_COASTING_THRESHOLD &&
      nextPeriod.coastingIntensity > SIGNIFICANT_COASTING_THRESHOLD

    const shouldForceToZero = isHardBraking || isHardAcceleration
    if (shouldForceToZero && !inCoastingSection) {
      smoothedPeriods[i] = {
        ...currentPeriod,
        coastingIntensity: 0,
      }
      continue
    }

    const windowStart = Math.max(
      0,
      i - Math.floor(FINAL_SMOOTHING_WINDOW_POINTS / 2)
    )
    const windowEnd = Math.min(
      smoothedPeriods.length - 1,
      i + Math.floor(FINAL_SMOOTHING_WINDOW_POINTS / 2)
    )

    const currentLean = Math.abs(currentPeriod.leanAngle ?? 0)
    let weightedIntensitySum = 0
    let weightSum = 0

    for (let j = windowStart; j <= windowEnd; j++) {
      const distance = Math.abs(i - j)
      let weight = Math.exp(
        (-distance * distance) /
          (FINAL_SMOOTHING_WINDOW_POINTS * FINAL_SMOOTHING_GAUSSIAN_SPREAD)
      )

      const neighborLean = Math.abs(smoothedPeriods[j].leanAngle ?? 0)
      const leanDifference = Math.abs(currentLean - neighborLean)

      // Why >5°? Different riding modes (straight vs corner) require separation
      if (leanDifference > 5) {
        const leanPenalty = Math.exp(-leanDifference / 15)
        weight *= leanPenalty
      }

      weightedIntensitySum += smoothedPeriods[j].coastingIntensity * weight
      weightSum += weight
    }

    if (weightSum > 0) {
      const smoothedIntensity = weightedIntensitySum / weightSum

      smoothedPeriods[i] = {
        ...smoothedPeriods[i],
        coastingIntensity: smoothedIntensity,
        acceleration: currentPeriod.acceleration,
      }
    }
  }

  return smoothedPeriods
}

/** Suppress weak coasting (<10%) created by smoothing artifacts. */
export const applyPostSmoothingCleanup = (
  periods: CoastingPeriod[]
): CoastingPeriod[] => {
  return periods.map((period) => {
    if (period.coastingIntensity < POST_SMOOTHING_SUPPRESSION_THRESHOLD) {
      return {
        ...period,
        coastingIntensity: 0,
      }
    }
    return period
  })
}
